/* hs-eslint ignored failing-rules */
/* eslint-disable hubspot-dev/no-unsafe-storage */

'use es6';

import I18n from 'I18n';
import { createSelector } from 'reselect';
import { getHasFreeEmail, usingEmailStarter } from 'EmailEditorUI/redux/selectors/AuthSelectors';
import { getIsDragDropEmail } from './emailCategoriesSelectors';
import { ONBOARDING_STEPS, MAYBE_LATER_SHOW_AGAIN_ONBOARDING, ONBOARDING_STEP_CRITERIA } from '../../constants/OnboardingConstants';
import { getAttributes } from 'ContentEditorUI/redux/selectors/userAttributesSelectors';
import { CURRENT_USER_ATTRIBUTES } from 'EmailEditorUI/constants/UserAttributesForEditor';
import { getOpenedPopover } from './AppStateSelectors';
import { POPOVERS } from '../../constants/PopoversConstants';
import { getIsContentTab } from 'ContentEditorUI/redux/selectors/appStatusSelectors';
import { basicSelector, basicSelectorWithStats } from 'ContentEditorUI/redux/selectors/helpers';
import { getIsReadOnlyMode } from 'EmailEditorUI/redux/selectors/ReadOnlySelector';
import { getIsAutomated } from './EmailSelectors';
import { getIsAutomationTab } from './previewSelectors';
import { getInteractionsWithContextualAutomation } from './WorkflowsSelectors';
import { getRoutingQueryParams } from 'ContentEditorUI/redux/selectors/routingSelectors';
import { MODAL_TYPES } from 'ContentEditorUI/lib/modalConstants';
import { getOpenModal } from 'ContentEditorUI/redux/selectors/modalSelectors';
const getHasNeverSeenCustomEditorOnboarding = basicSelectorWithStats(state => {
  const hasSeenOnboarding = getAttributes(state)[CURRENT_USER_ATTRIBUTES.HAS_SEEN_CUSTOM_ONBOARDING];
  return !hasSeenOnboarding || hasSeenOnboarding === 'false';
});
const getDismissedOnboardingTimestamp = basicSelectorWithStats(state => {
  const maybeLaterTimestampAttribute = getAttributes(state)[CURRENT_USER_ATTRIBUTES.DISMISSED_ONBOARDING_WITH_MAYBE_LATER_TIMESTAMP];
  return maybeLaterTimestampAttribute ? Number(maybeLaterTimestampAttribute) : null;
});
export const getShouldShowCustomOnboarding = basicSelectorWithStats(state => {
  const hasNeverSeenOnboarding = getHasNeverSeenCustomEditorOnboarding(state);
  const maybeLaterTimestamp = getDismissedOnboardingTimestamp(state);
  return hasNeverSeenOnboarding && (!maybeLaterTimestamp || maybeLaterTimestamp + MAYBE_LATER_SHOW_AGAIN_ONBOARDING < Date.now());
});
export const getOnboardingStep = basicSelector(state => {
  const {
    onboardingStatus
  } = state;
  return onboardingStatus ? onboardingStatus.currentStep : '';
});
export const getCurrentOnboarding = basicSelector(state => {
  const {
    onboardingStatus
  } = state;
  return onboardingStatus ? onboardingStatus.currentOnboarding : '';
});
export const getCurrentSteps = basicSelector(state => {
  const {
    onboardingStatus
  } = state;
  return onboardingStatus ? onboardingStatus.steps : '';
});
export const getIsStartedFromActions = basicSelector(state => {
  const {
    onboardingStatus
  } = state;
  return onboardingStatus ? onboardingStatus.isStartedFromActions : false;
});

// we only have one config right now - splitting this out to fix circular dep
export const makeGetCountedSteps = stepsConfig => createSelector(getCurrentSteps, currentSteps => currentSteps.filter(step => !stepsConfig[step].hideStepCount));
export const getIsOnboardingDismissed = basicSelector(state => {
  const {
    onboardingStatus
  } = state;
  return onboardingStatus && onboardingStatus.dismissed;
});
export const getShowOnboardingDismissModal = basicSelector(state => {
  const {
    onboardingStatus
  } = state;
  return onboardingStatus && onboardingStatus.showOnboardingDismiss;
});

// Only used for the onboarding DRAG_DROP
const getIsOnboardingAvailable = createSelector([getHasFreeEmail, usingEmailStarter, getIsDragDropEmail, getIsAutomated], (hasFreeEmail, hasStarterEmail, isDragDropEmail, isAutomated) => isDragDropEmail ? !isAutomated && (hasFreeEmail || hasStarterEmail) : true);
export const getFilteredOnboardingSteps = basicSelectorWithStats((state, onboardingName) => {
  return ONBOARDING_STEPS[onboardingName].filter(step => {
    return !ONBOARDING_STEP_CRITERIA[step] || ONBOARDING_STEP_CRITERIA[step](state);
  });
});
export const getIsOnboardingEnabled = basicSelector(state => {
  const queryParams = getRoutingQueryParams();
  return queryParams && queryParams.onboarding === 'true' && getIsOnboardingAvailable(state);
});
export const getIsOnboardingTourInProgress = basicSelector(state => {
  const {
    onboardingStatus
  } = state;
  return onboardingStatus.isOnboardingTourInProgress;
});
export const getIsOnboardingActive = createSelector([getOnboardingStep], onboardingStep => onboardingStep !== '');
export const getIsOnboardingCompleted = basicSelector(state => {
  return state.onboardingStatus && state.onboardingStatus.completed;
});
export const getHasSeenContextualAutomationOnboarding = basicSelector(state => getAttributes(state)[CURRENT_USER_ATTRIBUTES.HAS_SEEN_EMAIL_CONTEXTUAL_AUTOMATION_ONBOARDING] === 'true');
const getOnboardingDismissDateInDays = state => {
  const dismissedOnboardingDate = getAttributes(state)[CURRENT_USER_ATTRIBUTES.DISMISSED_CONTEXTUAL_AUTOMATION_ONBOARDING_WITH_MAYBE_LATER_TIMESTAMP];
  const date = I18n.moment(I18n.parseNumber(dismissedOnboardingDate));
  let duration;
  if (date.isValid()) {
    duration = I18n.moment.duration(I18n.moment().diff(date)).asDays();
  }
  return duration;
};
export const getHasDismissedOnboardingADayAgo = basicSelector(state => {
  const duration = getOnboardingDismissDateInDays(state);
  if (!duration) {
    return false;
  }
  return duration >= 1;
});
export const getHasDismissedOnboardingToday = basicSelector(state => {
  const duration = getOnboardingDismissDateInDays(state);
  if (!duration) {
    return false;
  }
  return duration <= 1;
});
export const getIsContentSidebarTabActive = basicSelector(state => {
  return state.sidebarTab ? state.sidebarTab.mainTab === 'content' : state.selected.currentTab === 'content';
});
export const getIsDesignTabActive = basicSelector(state => {
  return state.sidebarTab ? state.sidebarTab.mainTab === 'design' : state.selected.currentTab === 'design';
});
export const getIsTabWithPlainTextField = basicSelector(state => state.panesVisitStatus.currentPane === 'setup');
const getIsSendTabActive = basicSelector(state => {
  return state.panesVisitStatus.currentPane === 'send-options';
});
export const getShouldShowContextualAutomationOnboarding = createSelector([getHasSeenContextualAutomationOnboarding, getIsAutomationTab, getHasDismissedOnboardingToday, getInteractionsWithContextualAutomation, getIsReadOnlyMode], (hasSeenContextualAutomationOnboarding, isAutomationTab, hasDismissedOnboardingToday, interactionsWithContextualAutomation, isReadOnlyMode) => {
  if (hasSeenContextualAutomationOnboarding || !isAutomationTab || hasDismissedOnboardingToday || interactionsWithContextualAutomation > 0 || isReadOnlyMode || window.localStorage.getItem('SKIP_AUTOMATION_ONBOARDING')) {
    return false;
  }
  return true;
});
export const getShouldShowRepeatableContextualAutomationOnboarding = createSelector([getIsAutomationTab, getInteractionsWithContextualAutomation, getIsReadOnlyMode], (isAutomationTab, interactionsWithContextualAutomation, isReadOnlyMode) => {
  if (!isAutomationTab || interactionsWithContextualAutomation > 0 || isReadOnlyMode || window.localStorage.getItem('SKIP_AUTOMATION_ONBOARDING')) {
    return false;
  }
  return true;
});
export const shouldShowCustomModulesStep = createSelector([getIsContentTab, getIsContentSidebarTabActive], (isContentTab, contentSidebarTabActive) => isContentTab && contentSidebarTabActive);
export const shouldShowCustomEmailTemplateStep = createSelector([getIsContentTab, getIsDesignTabActive, getOpenedPopover], (isContentTab, designTabActive, openedPopover) => isContentTab && designTabActive && openedPopover === POPOVERS.TEMPLATE_SETTINGS_ACTIONS);
export const shouldShowCustomSmartRulesStep = getIsContentTab;
export const shouldShowEmailActionsStep = createSelector([getOpenedPopover], openedPopover => openedPopover === POPOVERS.EMAIL_HEADER_ACTIONS);
export const automationStepRenderRequirement = createSelector([shouldShowEmailActionsStep, getIsSendTabActive], (shouldShowEmailActionsStepValue, isSendTabActive) => {
  return shouldShowEmailActionsStepValue || isSendTabActive;
});
export const shouldShowPreviewStep = createSelector([getOpenedPopover], openedPopover => openedPopover === POPOVERS.EMAIL_PREVIEW_BUTTON);
export const shouldShowPlainTextToggleStep = createSelector([getOpenedPopover], openedPopover => openedPopover === POPOVERS.EMAIL_EDITOR_DISPLAY_OPTIONS);
export const getIsOnboardingModalOpen = createSelector([getOpenModal], openModal => {
  return [MODAL_TYPES.CONTEXTUAL_AUTOMATION_ONBOARDING_MODAL, MODAL_TYPES.EMAIL_CUSTOM_ONBOARDING_MODAL].includes(openModal);
});